import { Module } from "vuex";

import {
  API_BIS_LISTLAYOUT,
  API_BIS_CUSTOMFIELD_LIST,
  API_BIS_CATEGORY_TREE,
  API_BIS_FILTER_TREE,
} from "@/configs/Apis";

import { isEmpty, isEmptyOrWhiteSpace } from "@/utils/Common";
import DuduAjax from "@/utils/DuduAjax";

declare interface ProjectModule {
  info: any;
  metadata: Array<any>;
  listLayout: string;
  customCategoryTree: Array<any>;
  customFilterData: Array<any>;
}

const baseWidth = 140;

const defaultColumns = [
  { prop: "patentNo", label: "申请号", hidden: false },
  { prop: "patentType", label: "专利类型", hidden: false },
  { prop: "title", label: "专利名称", hidden: false },
  { prop: "modifiedTime", label: "导入时间", hidden: false },
  { prop: "appDate", label: "申请日", hidden: false },
  { prop: "pubNo", label: "公开(公告)号", hidden: false },
  { prop: "pubDate", label: "公开(公告)日", hidden: false },
  { prop: "authDate", label: "授权日", hidden: false },
  { prop: "country", label: "专利所属国", hidden: false },
  { prop: "patentee", label: "当前专利权人", hidden: false },
  { prop: "applicants", label: "申请人", hidden: false, width: 180 },
  { prop: "primaryApplicantName", label: "主申请人", hidden: false, width: 180 },
  { prop: "applicantCountry", label: "申请人(国家)", hidden: false },
  { prop: "applicantProvince", label: "申请人(省)", hidden: false },
  { prop: "applicantCity", label: "申请人(市)", hidden: false },
  { prop: "applicantArea", label: "申请人(区县)", hidden: false },
  { prop: "applicantAddress", label: "申请人地址", hidden: false },
  { prop: "inventors", label: "发明人", hidden: false },
  { prop: "primaryInventorName", label: "主发明人", hidden: false, width: 180 },
  { prop: "agencyName", label: "代理机构", hidden: false },
  { prop: "agentName", label: "代理人", hidden: false },
  { prop: "priorityPatents", label: "优先权", hidden: false },
  { prop: "inteAppDate", label: "国际申请日", hidden: false },
  { prop: "inteAppNo", label: "国际申请号", hidden: false },
  { prop: "intePubDate", label: "国际公布日", hidden: false },
  { prop: "intePubNo", label: "国际公布号", hidden: false },
  { prop: "legalStatus", label: "法律状态", hidden: false },
  { prop: "primaryIpc", label: "IPC主分类", hidden: false },
  { prop: "miniIpc", label: "IPC小类", hidden: false },
  { prop: "shortIpc", label: "IPC大类", hidden: false },
  { prop: "ipc", label: "IPC", hidden: false },
  { prop: "cpc", label: "CPC", hidden: false },
  { prop: "ecla", label: "ECLA", hidden: false },
];

const module: Module<ProjectModule, any> = {
  namespaced: true,
  state: {
    info: null,
    metadata: [],
    listLayout: "",
    customCategoryTree: [],
    customFilterData: [],
  },

  getters: {
    customfields(state) {
      const map: Map<string, number> = new Map([
        ["VARCHAR", 0],
        ["ENUM", 100],
        ["TEXT", 200],
      ]);
      return JSON.parse(JSON.stringify(state.metadata)).sort((item1: any, item2: any) => {
        const left = map.get(item1.fieldType) || 0;
        const right = map.get(item2.fieldType) || 0;
        let seed1 = 0;
        let seed2 = 0;

        let match = /_(\d+)$/g.exec(item1.fieldName);
        if (match && match.length) {
          seed1 = parseInt(match[1]);
        }

        match = /_(\d+)$/g.exec(item2.fieldName);
        if (match && match.length) {
          seed2 = parseInt(match[1]);
        }
        return left + seed1 - (right + seed2);
      });
    },
  },

  mutations: {
    info(state, payload) {
      state.info = payload;
    },
    metadata(state, payload) {
      state.metadata = payload;
    },
    listLayout(state, payload) {
      state.listLayout = payload;
    },
    customCategoryTree(state, payload) {
      state.customCategoryTree = payload;
    },
    customFilterData(state, payload) {
      state.customFilterData = payload;
    },
  },

  actions: {
    async setProjectInfo({ commit, dispatch }, payload) {
      commit("info", payload);
      await dispatch("setCustomField", payload);
      await Promise.all([
        dispatch("setListLayout", payload),
        dispatch("getCustomCategoryData", payload),
        dispatch("getCustomFilterData", payload),
      ]);
    },

    async setListLayout({ commit, state }, payload) {
      const layout = await DuduAjax.get<string>(API_BIS_LISTLAYOUT, { listName: "PATENT", projectId: payload.id });
      let layoutObj: Array<any>;
      if (isEmptyOrWhiteSpace(layout)) {
        layoutObj = JSON.parse(JSON.stringify(defaultColumns));
      } else {
        layoutObj = JSON.parse(layout || "[]");
      }
      const customised = state.metadata;
      const customisedMap = new Map();
      if (customised && customised.length) {
        customised.forEach((field) => customisedMap.set(field.fieldName.replace("_", ""), field));
      }

      layoutObj.forEach((item, index) => {
        // 删除不存在的字段
        if (/^(enum|varchar|text)_?\d+$/g.test(item.prop) && !customisedMap.has(item.prop)) {
          layoutObj[index] = null;
        }
        // 如果存在从map中删除
        if (customisedMap.has(item.prop)) {
          item.label = customisedMap.get(item.prop).label;
          customisedMap.delete(item.prop);
        }
      });

      customisedMap.forEach((value, key) =>
        layoutObj.push({ prop: key.replace("_", ""), label: value.label, hidden: true })
      );
      layoutObj = layoutObj.filter((field) => !isEmpty(field));
      layoutObj.forEach((field) => {
        if (!Object.hasOwn(field, "width")) {
          field.width = baseWidth;
        }
      });
      commit("listLayout", JSON.stringify(layoutObj));
    },

    async setCustomField({ commit }, payload) {
      const customField = await DuduAjax.get(API_BIS_CUSTOMFIELD_LIST, { projectId: payload.id });
      commit("metadata", customField);
    },

    async getCustomCategoryData({ commit, state }) {
      const data = await DuduAjax.get<Array<any>>(
        {
          url: API_BIS_CATEGORY_TREE,
          path: { projectId: state.info.id },
        },
        {},
        false
      );
      const treeData: Array<any> = [];
      const map = new Map<string, any>();
      if (data && data.length) {
        data.forEach((item) => {
          if (item.treeLevel === 0) {
            return;
          }
          let treeItem;
          if (map.has(item.id)) {
            treeItem = map.get(item.id);
            if (isEmptyOrWhiteSpace(treeItem.title)) {
              Object.assign(treeItem, item);
            }
          } else {
            treeItem = { name: item.name, ...item };
            map.set(item.id, treeItem);
          }
          if (item.treeLevel === 1) {
            treeData.push(treeItem);
          } else {
            const pId = item.parentId;
            if (map.has(pId)) {
              const pItem = map.get(pId);
              if (!Object.hasOwn(pItem, "children")) {
                pItem.children = [];
              }
              pItem.children.push(treeItem);
            } else {
              map.set(pId, { children: [treeItem] });
            }
          }
        });
      }
      commit("customCategoryTree", treeData);
    },

    async getCustomFilterData({ commit, state }) {
      const data = await DuduAjax.get<Array<any>>(
        {
          url: API_BIS_FILTER_TREE,
          path: { projectId: state.info.id },
        },
        {},
        false
      );
      commit("customFilterData", data);
    },

    clearProject({ commit }) {
      commit("info", null);
      commit("metadata", []);
      commit("listLayout", "");
      commit("customCategoryTree", []);
    },
  },
};

export default module;
